import { Button, ToastProvider } from "@ondit/ui";
import { QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { AccountProvider, useAccount } from "~/common/account/account";
import { ClaimPage } from "~/common/auth/claim";
import { InvitePage, ResetPage } from "~/common/auth/link";
import { LoginPage } from "~/common/auth/login";
import { NewBrandPage, OnboardingPage } from "~/common/auth/onboarding";
import { TopUpProvider, useTopUp } from "~/common/billing/topup";
import { settingsHref } from "~/common/settings/catalog";
import { SettingsPage } from "~/common/settings/settings";
import { StudioPage } from "~/common/studio/studio";
import { TaskCenter } from "~/common/tasks/tasks";
import { AccessProvider } from "~/core/access";
import { events, type Me, type Org, type OrgEntity } from "~/core/api";
import { useStored } from "~/core/hooks";
import { I18nProvider, type Locale, pick, readLocale, useT, writeLocale } from "~/core/i18n";
import { settings } from "~/core/paths";
import { getQueryClient, keys, ORG_EVENTS, scopesFor } from "~/core/query";
import { GateListener, isAdmin, SessionGate } from "~/core/session";
import { RouteBoundary } from "~/core/ui/boundary";
import { Async, Crashed, Empty, type Reading } from "~/core/ui/states";
import { brandsQuery, currentOrg, Workspaces } from "~/core/workspace";
import { agentHelpRoute, agentNavigation, agentRoutes } from "~/modules/agent/routes";
import { DetailProvider } from "~/modules/analytics/components/detail";
import { BrandEnginesProvider } from "~/modules/analytics/components/filters";
import { useFilters } from "~/modules/analytics/lib/filters";
import { AnalyticsNavigation } from "~/modules/analytics/navigation";
import { AnalyticsRoutes } from "~/modules/analytics/routes";
import { AppShell, Banners } from "~/shell/app-shell";
import { kindOf, type Module, moduleHome, useModule } from "~/shell/module";

/**
 * One entry, one router, one shell.
 *
 * Above the brand: signing in, the two links that land somebody who has no
 * session yet, and help. Under `/w/:brandId`: the two modules, Analytics at the
 * root of the brand and the Agent one segment below it.
 */
export function App() {
	const [locale, setLocaleState] = useState<Locale>(() => readLocale());
	const setLocale = useCallback((next: Locale) => {
		setLocaleState(next);
		writeLocale(next);
	}, []);
	useEffect(() => {
		document.documentElement.lang = pick(locale, ["en", "zh-CN"]);
	}, [locale]);

	return (
		// One cache for both modules. The Agent's code reaches the same client
		// by import from outside React, so this has to be that client and not
		// a second one of the panel's own.
		<QueryClientProvider client={getQueryClient()}>
			<I18nProvider locale={locale} setLocale={setLocale}>
				<Routes>
					<Route path="/login" element={<LoginPage />} />
					{/* Where the reset link lands. Outside the shell: nobody following it
				    is signed in yet. */}
					<Route path="/reset" element={<ResetPage />} />
					{/* And where an invitation lands, for the same reason twice over: the
				    person following it may have no account here at all, and the
				    account they are being invited to is not one the shell could load
				    for them yet. */}
					<Route path="/invite" element={<InvitePage />} />
					{/* Help, for the same reason a third time: it is what somebody reads
				    when the account will not let them in. */}
					{agentHelpRoute()}
					{/* The last resort. Every screen below is inside a boundary of its
					    own; this one catches what those cannot — the shell, the
					    providers, the brand list — which without it took the document
					    with them. */}
					<Route
						path="/*"
						element={
							<RouteBoundary fallback={Crashed}>
								<SessionGate>{(me) => <Account me={me} />}</SessionGate>
							</RouteBoundary>
						}
					/>
				</Routes>
				<ToastProvider />
			</I18nProvider>
		</QueryClientProvider>
	);
}

function Account({ me }: { me: Me }) {
	const t = useT();
	const [chosen, setChosen] = useStored<string>("ondit.org", "");
	const org = currentOrg(me.orgs, chosen);

	if (!org)
		return (
			<div className="content">
				<Empty title={t("empty.title")} body={t("empty.body")} />
			</div>
		);

	return (
		<Workspaces.Provider value={{ orgs: me.orgs, current: org, choose: setChosen }}>
			{/* Keyed on the account, so a switch tears the old one down rather than
			    drawing its brands over the new one until the new list arrives —
			    which is how a person ended up one click from writing into the wrong
			    account. */}
			<OrgWorkspace key={org.id} me={me} org={org} />
		</Workspaces.Provider>
	);
}

function OrgWorkspace({ me, org }: { me: Me; org: Org }) {
	const t = useT();
	const queryClient = useQueryClient();
	const brands = useQuery(brandsQuery(org.id));
	const orgId = org.id;

	// Live events from the worker: a batch finished, a page published. Each
	// marks what it changed as stale and whatever is on screen reads again.
	// Without this the panel would have to poll, and the interesting jobs take
	// minutes.
	useEffect(() => {
		let source: EventSource | null = null;
		try {
			source = events(orgId);
		} catch {
			// EventSource is unavailable or the origin refused it. The application
			// still works; it just will not update on its own.
			return;
		}
		const stale = (event: Event) => {
			const { type, data } = event as MessageEvent<string>;
			for (const queryKey of scopesFor(type, data, orgId)) void queryClient.invalidateQueries({ queryKey });
		};
		for (const kind of ORG_EVENTS) source.addEventListener(kind, stale);
		return () => source?.close();
	}, [orgId, queryClient]);

	// A payment moves the balance, which is on the person's row and the
	// account's; a verified claim adds a brand.
	const paid = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: keys.me });
		void queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
	}, [queryClient, orgId]);
	const claimed = useCallback(
		() => void queryClient.invalidateQueries({ queryKey: keys.brands(orgId) }),
		[queryClient, orgId],
	);

	const admin = isAdmin(me, org);
	// Zero is the enterprise row: a number the contract sets, not this shell.
	const brandsFull = org.entities_max > 0 && (brands.data?.length ?? org.entity_count) >= org.entities_max;

	return (
		// Around every route below, because a refusal raised on any of them
		// offers one of the two ways out: a pack, or the plan.
		<TopUpProvider org={org} admin={admin} onPaid={paid}>
			<AccountProvider firstBrand={brands.data?.[0]?.id}>
				<Gates />
				<Routes>
					{/*
					 * Outside the brand list on purpose. Someone arriving from a
					 * "Claim this listing" link on the directory has no brands yet, and
					 * the list's empty state would swallow the route they came for.
					 */}
					<Route
						path="/claim/:slug"
						element={
							<div className="content">
								<ClaimRoute orgId={org.id} onVerified={claimed} />
							</div>
						}
					/>
					<Route path="/onboarding" element={<OnboardingPage />} />
					{/*
					 * The second brand, and the tenth. Outside the brand list for the
					 * same reason claiming is: the person is here to create the thing
					 * the list would otherwise be asking them to pick.
					 */}
					<Route
						path="/brands/new"
						element={
							!org.onboarded_at ? (
								<Navigate to="/onboarding" replace />
							) : !admin ? (
								// A member cannot add a brand; the route would refuse the
								// wizard's first write, after it had spent a model call
								// analysing the site.
								<div className="content">
									<Empty title={t("brand.add")} body={t("access.admin")} />
								</div>
							) : brandsFull ? (
								<BrandsFull max={org.entities_max} />
							) : (
								<NewBrandPage orgId={org.id} />
							)
						}
					/>
					{/*
					 * An org that has not finished the wizard is sent to it from every
					 * other path. The wizard writes each step to the server, so this
					 * resumes rather than restarts.
					 */}
					<Route
						path="*"
						element={
							org.onboarded_at ? (
								<BrandWorkspace me={me} org={org} brands={brands} />
							) : (
								<Navigate to="/onboarding" replace />
							)
						}
					/>
				</Routes>
			</AccountProvider>
		</TopUpProvider>
	);
}

/** The application's answer to a refusal, wired to the two things that resolve
 *  one. Inside both providers, because it opens what they render. */
function Gates() {
	const topUp = useTopUp();
	const openAccount = useAccount();
	// Read during render, before the `/billing` redirect below replaces the URL.
	// The site's account menu offers "buy credits", and buying is a dialog rather
	// than a screen, so a query string is the only address it has.
	const [wantsTopUp] = useState(() => new URLSearchParams(window.location.search).has("topup"));
	useEffect(() => {
		if (wantsTopUp) topUp();
	}, [wantsTopUp, topUp]);
	return (
		<GateListener
			onCredits={useCallback(() => topUp("credits"), [topUp])}
			onSubscription={useCallback(() => openAccount("subscription"), [openAccount])}
		/>
	);
}

/**
 * The plan's brand limit, met. The empty state says so and leads to the plan,
 * instead of opening a wizard whose first write would be refused — after it had
 * spent a model call analysing the site.
 */
function BrandsFull({ max }: { max: number }) {
	const t = useT();
	const openAccount = useAccount();
	return (
		<div className="content">
			<Empty
				title={t("brand.full", { n: String(max) })}
				action={
					<Button type="button" variant="primary" onClick={() => openAccount("subscription")}>
						{t("brand.upgrade")}
					</Button>
				}
			/>
		</div>
	);
}

function ClaimRoute({ orgId, onVerified }: { orgId: string; onVerified: () => void }) {
	const { slug } = useParams();
	const navigate = useNavigate();
	return (
		<ClaimPage
			slug={slug ?? ""}
			orgId={orgId}
			onVerified={(brandId) => {
				onVerified();
				navigate(`/w/${brandId}`);
			}}
		/>
	);
}

/**
 * Which half of the product this account opens on.
 *
 * The Agent is a plan feature, and on a plan without it the mode switch, its
 * routes and its column are not drawn at all — so every address that means
 * "this brand's workspace" has to resolve to the other half.
 */
function front(org: Org): Module {
	return org.agent ? "agent" : "analytics";
}

function BrandWorkspace({ me, org, brands }: { me: Me; org: Org; brands: Reading<OrgEntity[]> }) {
	const t = useT();
	const admin = isAdmin(me, org);
	return (
		<Async
			state={brands}
			empty={
				<AccessProvider
					value={{
						editor: true,
						admin,
						subscribed: org.subscription_state === "active",
						operable: org.subscription_state !== "expired",
						agent: org.agent,
						advanced: org.advanced_insights,
					}}
				>
					<EmptyAccount me={me} org={org} admin={admin} />
				</AccessProvider>
			}
		>
			{(list) => (
				<Routes>
					{/* The Agent, not the brand's bare path: the front of the product is
					    the thing you ask, and Analytics is where you go to read what it
					    found. Its own first row is `home`, which is what `moduleHome`
					    resolves to. On a plan without the Agent there is no such front
					    door, and the brand's workspace is Analytics. */}
					<Route path="/" element={<Navigate to={moduleHome(list[0].id, front(org))} replace />} />
					<Route path="/w/:workspaceId/*" element={<Brand me={me} org={org} brands={list} />} />
					{/* The plan's one address that names no brand: the server writes
					    it into billing mail and hands it to paygate as the return from
					    checkout, and neither knows which brand somebody was reading. */}
					<Route path="/billing" element={<Navigate to={settings(list[0].id, "subscription")} replace />} />
					{/* The same, for the site's account menu, which has no brand to name either. */}
					<Route path="/profile" element={<Navigate to={settings(list[0].id, "profile")} replace />} />
					{/* Where settings lives while the account has no brand. A brand
					    arrived since — claimed, or created — so the page is the
					    brand's now, open on the section that was being read. */}
					<Route path="/settings/:section" element={<BrandSettingsRedirect brandId={list[0].id} />} />
					<Route path="*" element={<Navigate to={moduleHome(list[0].id, front(org))} replace />} />
				</Routes>
			)}
		</Async>
	);
}

/** The brand-less settings address, once the account has a brand. */
function BrandSettingsRedirect({ brandId }: { brandId: string }) {
	const { section } = useParams();
	return <Navigate to={settingsHref(brandId, section)} replace />;
}

function EmptyAccount({ me, org, admin }: { me: Me; org: Org; admin: boolean }) {
	const t = useT();
	const navigate = useNavigate();
	return (
		<AppShell
			me={me}
			org={org}
			brands={[]}
			canAddBrand={admin}
			onAddBrand={() => navigate("/brands/new")}
			// Settings is the account's as much as the brand's, and the account is
			// what somebody with no brand has: the plan to pay for, the people to
			// invite, their own language. The page drops the brand's sections.
			onOpenSettings={() => navigate(settings("", "profile"))}
			pane="padded"
			navigation={null}
		>
			<Routes>
				<Route path="/settings/:section" element={<SettingsPage me={me} org={org} brandId="" />} />
				<Route path="/settings" element={<Navigate to={settings("", "profile")} replace />} />
				{/* The two addresses that name no brand, and now have none to be sent to. */}
				<Route path="/billing" element={<Navigate to={settings("", "subscription")} replace />} />
				<Route path="/profile" element={<Navigate to={settings("", "profile")} replace />} />
				<Route
					path="*"
					element={
						/* An onboarded account with nothing in it: describing a brand is
						   the way out, not the directory — for somebody who may add one.
						   A member is told who can. */
						<Empty
							title={t("empty.title")}
							body={admin ? t("empty.body") : t("access.admin")}
							action={
								admin ? (
									<Button type="button" variant="primary" onClick={() => navigate("/brands/new")}>
										{t("brand.add")}
									</Button>
								) : undefined
							}
						/>
					}
				/>
			</Routes>
		</AppShell>
	);
}

function Brand({ me, org, brands }: { me: Me; org: Org; brands: OrgEntity[] }) {
	const { workspaceId: brandId = "" } = useParams();
	const navigate = useNavigate();
	const brand = brands.find((one) => one.id === brandId);
	const [filters, setFilters] = useFilters(brand?.id ?? brandId, brand?.locales);

	// A brand this account does not have — a bookmark from another workspace, or
	// a brand since removed. Sent to the first one, and the address bar says so,
	// rather than drawing the first brand under the other brand's URL.
	if (!brand) return <Navigate to={brands[0] ? moduleHome(brands[0].id, front(org)) : "/"} replace />;

	const admin = isAdmin(me, org);

	return (
		// Around the whole workspace rather than inside each screen: a drill-down
		// opened from the overview can lead to a rival, and from there to an
		// answer, and a drawer owned by the screen that opened it would be torn
		// down by the first navigation that stack contains.
		<AccessProvider
			value={{
				editor: brand.role === "editor",
				// A platform operator working through the panel: support, and the
				// reason this is not the security boundary. The routes ask again.
				admin,
				// A lapsed plan reads everything and changes nothing; the ways out —
				// paying, pausing, cancelling — do not ask this.
				subscribed: org.subscription_state === "active",
				operable: org.subscription_state !== "expired",
				agent: org.agent,
				advanced: org.advanced_insights,
			}}
		>
			<BrandEnginesProvider engines={brand.engines}>
				<DetailProvider entityId={brand.id} filters={filters}>
					<Routes>
						{/*
						 * One frame for both modules, and the reason this is a single
						 * layout route rather than two: the Agent used to be mounted
						 * beside the shell, so crossing between the modules tore the
						 * left column down and built the other module's. Under one
						 * route the column is mounted once and only its middle
						 * changes.
						 */}
						<Route
							element={
								/* Which engines this brand is measured on, for every filter bar
								   under here. The list comes down with the brand rather than
								   being asked for per screen: it does not vary within a brand,
								   and fifteen screens each fetching it is fifteen requests for
								   one answer. */
								<BrandShell me={me} org={org} brands={brands} brand={brand} admin={admin} />
							}
						>
							<Route path="studio/:itemId" element={<StudioPage brandId={brand.id} />} />
							{org.agent ? (
								agentRoutes()
							) : (
								/* A bookmark from a plan that had it. The brand's workspace,
								   not a not-found: the screens are gone, the brand is not. */
								<Route path="agent/*" element={<Navigate to={`/w/${brand.id}`} replace />} />
							)}
							{/*
							 * Settings is the shell's rather than either module's: it holds
							 * the brand's own filing, the account's plan and the Agent's one
							 * workspace resource, and no module owns all three. It sits under
							 * Analytics' navigation because that is where its row is. The
							 * bare path — the sidebar's row, every `${base}/settings` link in
							 * Analytics — lands on the brand, which is what those links are
							 * about.
							 */}
							<Route path="settings" element={<Navigate to={settings(brand.id, "identity")} replace />} />
							<Route path="settings/:section/*" element={<SettingsPage me={me} org={org} brandId={brand.id} />} />
							{/*
							 * Tasks is the other screen that is neither module's: one list
							 * with a tab for each side, and the Agent's sidebar links
							 * straight into its own. It sat inside `AnalyticsRoutes` until
							 * 2026-09-12, which is why opening it from the Agent replaced
							 * the whole left column with Analytics' five stages.
							 */}
							<Route
								path="tasks/*"
								element={
									<TaskCenter
										orgId={org.id}
										brandId={brand.id}
										agent={org.agent}
										brands={brands.map((one) => ({ entity_id: one.id, name: one.name }))}
									/>
								}
							/>
							<Route
								path="*"
								element={<AnalyticsRoutes orgId={org.id} brand={brand} filters={filters} setFilters={setFilters} />}
							/>
						</Route>
					</Routes>
				</DetailProvider>
			</BrandEnginesProvider>
		</AccessProvider>
	);
}

/**
 * The frame both modules render in, and the one thing that changes between
 * them: the middle of the left column.
 *
 * Mounted once for the brand, so crossing between Analytics and the Agent keeps
 * the column's scroll position, its open sections and whatever the reader was
 * hovering. Which module the pane is showing is not a question about the route
 * that matched: the Agent occupies one segment under a brand, Analytics is most
 * of what is left, and Settings and Tasks are neither — they keep whichever
 * module opened them.
 */
function BrandShell({
	me,
	org,
	brands,
	brand,
	admin,
}: {
	me: Me;
	org: Org;
	brands: OrgEntity[];
	brand: OrgEntity;
	admin: boolean;
}) {
	const navigate = useNavigate();
	// Two different questions. Which column to draw is the module, and on
	// Settings and Tasks that is the one the reader came from rather than
	// anything the path says. Whether the pane is padded is about the screen:
	// only the Agent's own are canvases. See `~/shell/module`.
	const onAgent = useModule() === "agent" && org.agent;
	const topUp = useTopUp();
	const bare = kindOf(useLocation().pathname) === "agent";
	return (
		<AppShell
			me={me}
			org={org}
			brands={brands}
			brandId={brand.id}
			canAddBrand={admin}
			onAddBrand={() => navigate("/brands/new")}
			onOpenSettings={() => navigate(settings(brand.id, "profile"))}
			onOpenBrandSettings={() => navigate(settings(brand.id, "identity"))}
			pane={bare ? "bare" : "padded"}
			navigation={onAgent ? agentNavigation() : <AnalyticsNavigation base={`/w/${brand.id}`} />}
			banners={
				<Banners
					org={org}
					brand={brand}
					onOpenPlan={() => navigate(settings(brand.id, "subscription"))}
					onTopUp={topUp}
				/>
			}
		>
			{/* Around the pane rather than around the shell: a screen that throws
			    costs its own middle and leaves the column to navigate away from,
			    which is what the Agent's surfaces have had since 2026-09-12 and
			    Analytics, Settings and Tasks did not. */}
			<RouteBoundary fallback={Crashed}>
				<Outlet />
			</RouteBoundary>
		</AppShell>
	);
}
